import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Home, Search, PlusSquare, MessageSquare, User, LogOut, Heart, ShieldCheck } from "lucide-react";
import { MatproLogo } from "@/components/MatproLogo";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { NAV } from "@/constants/testIds";
import { Footer } from "@/components/Footer";
import { NotificationsBell } from "@/components/NotificationsBell";
import { LangSwitcher } from "@/components/LangSwitcher";
import { EmailVerificationBanner } from "@/components/EmailVerificationBanner";
import { BackButton, shouldShowBackButton } from "@/components/BackButton";

const STAFF_ROLES = new Set(["admin"]);

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthed = !!user && user !== false;
  const isStaff = isAuthed && STAFF_ROLES.has(user.role);
  const [unread, setUnread] = useState(0);
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isMessagesRoute =
    location.pathname.startsWith("/messages") || location.pathname.startsWith("/messagerie");
  const showGlobalVerifyBanner =
    isAuthed &&
    !user.email_verified &&
    !["/profil", "/verifier-email", "/confirmation-email", "/parametres"].includes(location.pathname);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!isAuthed) { setUnread(0); return; }
      try {
        const { data } = await api.get("/conversations/unread-count");
        if (!cancelled) setUnread(data?.unread || 0);
      } catch {}
    };
    load();
    const iv = setInterval(load, 20000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isAuthed]);

  const activePath = (p) => location.pathname === p || (p !== "/" && location.pathname.startsWith(p));

  if (isAdminRoute) {
    return (
      <div className="min-h-screen bg-white text-matpro-anthracite">
        <a href="#main-content" className="matpro-skip-link">{t("a11y.skip_to_content")}</a>
        <main id="main-content" tabIndex={-1}>{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-matpro-bg text-matpro-anthracite">
      <a href="#main-content" className="matpro-skip-link">{t("a11y.skip_to_content")}</a>
      <header className="sticky top-0 z-40 border-b border-matpro-border/80 bg-white/95 backdrop-blur-md supports-[backdrop-filter]:bg-white/85">
        <div className="matpro-container flex h-[64px] min-w-0 items-center gap-3 md:h-[76px] md:gap-3">
          {/* Left — brand (priorité mobile) */}
          <div className="flex min-w-0 flex-1 items-center gap-1 md:flex-none">
            <MatproLogo />
            <nav
              className="ml-1 hidden items-center gap-1 md:flex"
              aria-label="Navigation principale"
            >
              <HeaderIconButton
                onClick={() => navigate("/")}
                testId={NAV.desktopHome}
                label={t("nav.home_label")}
              >
                <Home className="h-4 w-4" />
              </HeaderIconButton>
              <LangSwitcher />
              <span className="mx-1.5 h-4 w-px shrink-0 bg-matpro-border" aria-hidden />
              <HeaderNavLink
                active={activePath("/recherche") || activePath("/annonces")}
                onClick={() => navigate("/recherche")}
                testId="nav-search"
              >
                {t("nav.search")}
              </HeaderNavLink>
              <HeaderNavLink
                active={activePath("/tarifs")}
                onClick={() => navigate("/tarifs")}
                testId="nav-pricing"
              >
                {t("nav.pricing")}
              </HeaderNavLink>
            </nav>
          </div>

          {/* Middle — Déposer stretches between Tarifs and account icons */}
          <div className="hidden min-w-0 flex-1 md:block">
            <Button
              data-testid={NAV.desktopPostAd}
              className="h-12 w-full rounded-xl bg-matpro-green px-6 text-[15px] font-bold text-white shadow-none transition-colors duration-200 hover:bg-matpro-green-dark"
              onClick={() => navigate(isAuthed ? "/deposer" : "/connexion")}
            >
              + {t("nav.post_ad")}
            </Button>
          </div>

          {/* Right — account tools (desktop) + mobile lang/login */}
          <div className="ml-auto flex shrink-0 items-center justify-end gap-2 sm:gap-2.5 md:ml-0">
            <div className="hidden items-center gap-1 md:flex">
              {isAuthed ? (
                <>
                  <HeaderIconButton
                    onClick={() => navigate("/favoris")}
                    testId="nav-favorites"
                    label={t("nav.favorites_label")}
                  >
                    <Heart className="h-4 w-4" />
                  </HeaderIconButton>
                  <HeaderIconButton
                    onClick={() => navigate("/messages")}
                    testId="nav-messages"
                    label={unread > 0 ? t("nav.messages_unread").replace("{n}", String(unread)) : t("nav.messages_label")}
                    badge={unread}
                  >
                    <MessageSquare className="h-4 w-4" />
                  </HeaderIconButton>
                  <NotificationsBell />
                  {isStaff && (
                    <HeaderIconButton
                      onClick={() => navigate("/admin")}
                      testId="nav-admin"
                      label={t("nav.admin_label")}
                    >
                      <ShieldCheck className="h-4 w-4" />
                    </HeaderIconButton>
                  )}
                  <Button
                    data-testid={NAV.desktopProfile}
                    variant="outline"
                    className="ml-1 h-9 rounded-lg border-matpro-border px-3 text-sm font-medium text-matpro-anthracite transition-colors duration-200 hover:border-matpro-green/40 hover:bg-matpro-green-light/50 hover:text-matpro-green"
                    onClick={() => navigate("/profil")}
                  >
                    <User className="mr-1.5 h-4 w-4" />
                    {t("nav.profile")}
                  </Button>
                  <button
                    type="button"
                    data-testid={NAV.desktopLogout}
                    onClick={logout}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-matpro-anthracite-muted transition-colors duration-200 hover:bg-matpro-bg hover:text-matpro-anthracite"
                    aria-label={t("nav.logout")}
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <Button
                    data-testid={NAV.desktopLogin}
                    variant="outline"
                    className="h-9 rounded-lg border-matpro-border px-3.5 text-sm font-medium text-matpro-anthracite transition-colors duration-200 hover:border-matpro-green/40 hover:bg-white hover:text-matpro-green"
                    onClick={() => navigate("/connexion")}
                  >
                    {t("nav.login")}
                  </Button>
                  <Button
                    data-testid={NAV.desktopRegister}
                    variant="outline"
                    className="h-9 rounded-lg border-matpro-border px-3.5 text-sm font-medium text-matpro-anthracite transition-colors duration-200 hover:border-matpro-green/40 hover:bg-matpro-green-light/60 hover:text-matpro-green"
                    onClick={() => navigate("/inscription")}
                  >
                    {t("nav.register")}
                  </Button>
                </>
              )}
            </div>

            {/* Mobile — langue + connexion uniquement (nav du bas pour le reste) */}
            <div className="flex shrink-0 items-center gap-0.5 md:hidden">
              <LangSwitcher />
              {!isAuthed ? (
                <Button
                  data-testid={`${NAV.desktopLogin}-mobile`}
                  variant="outline"
                  className="h-10 min-h-10 rounded-lg border-matpro-border px-2.5 text-xs font-semibold text-matpro-anthracite sm:px-3 sm:text-sm"
                  onClick={() => navigate("/connexion")}
                >
                  {t("nav.login")}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <main
        id="main-content"
        tabIndex={-1}
        className={[
          isMessagesRoute
            ? "pb-[calc(4.25rem+env(safe-area-inset-bottom,0px))] md:pb-0"
            : "pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] md:pb-0",
        ].join(" ")}
      >
        {showGlobalVerifyBanner && !isMessagesRoute ? (
          <div className="matpro-container pt-4">
            <EmailVerificationBanner />
          </div>
        ) : null}
        {shouldShowBackButton(location.pathname) ? (
          <div className="matpro-container pt-4 md:pt-5">
            <BackButton />
          </div>
        ) : null}
        {children}
      </main>

      {!isMessagesRoute ? (
        <div className="pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] md:pb-0">
          <Footer />
        </div>
      ) : null}

      {/* Mobile Bottom Navigation */}
      <nav
        className="matpro-bottom-nav fixed bottom-0 left-0 right-0 z-50 border-t border-matpro-border bg-white flex justify-around pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom,0px))] shadow-[0_-2px_10px_rgba(0,0,0,0.04)] md:hidden"
        aria-label={t("nav.aria_mobile")}
      >
        <BottomTab to="/" testId={NAV.mobileHome} active={location.pathname === "/"} icon={Home} label={t("nav.home_label")} />
        <BottomTab to="/recherche" testId={NAV.mobileSearch} active={activePath("/recherche")} icon={Search} label={t("nav.search")} />
        <BottomTabAction
          to={isAuthed ? "/deposer" : "/connexion"}
          testId={NAV.mobilePost}
          icon={PlusSquare}
          label={t("nav.post_label")}
        />
        <BottomTab to="/messages" testId={NAV.mobileMessages} active={activePath("/messages")} icon={MessageSquare} label={unread > 0 ? t("nav.messages_unread").replace("{n}", String(unread)) : t("nav.messages_label")} badge={unread} />
        <BottomTab to={isAuthed ? "/profil" : "/connexion"} testId={NAV.mobileProfile} active={activePath("/profil")} icon={User} label={t("nav.profile_label")} />
      </nav>
    </div>
  );
}

function HeaderNavLink({ children, active, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={[
        "matpass-nav-link group relative inline-flex h-9 items-center px-2.5 text-[13.5px] font-medium tracking-[-0.01em] transition-colors duration-200",
        active ? "text-matpro-green-dark" : "text-matpro-anthracite hover:text-matpro-green-dark",
      ].join(" ")}
    >
      {children}
      <span
        className={[
          "pointer-events-none absolute inset-x-2.5 bottom-[5px] h-[2px] origin-left rounded-full bg-matpro-green transition-transform duration-300 ease-out",
          active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100",
        ].join(" ")}
        aria-hidden
      />
    </button>
  );
}

function HeaderIconButton({ children, onClick, testId, label, badge = 0 }) {
  const ariaLabel = badge > 0 ? `${label}, ${badge}` : label;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      className="relative inline-flex h-11 w-11 min-h-11 min-w-11 items-center justify-center rounded-lg text-matpro-anthracite-muted transition-colors duration-200 hover:bg-matpro-green-light/70 hover:text-matpro-green md:h-9 md:w-9 md:min-h-9 md:min-w-9"
    >
      {children}
      {badge > 0 && (
        <span className="absolute right-0.5 top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-matpro-green px-1 text-[9px] font-bold text-white">
          {badge}
        </span>
      )}
    </button>
  );
}

function BottomTab({ to, active, icon: Icon, label, testId, badge }) {
  const ariaLabel = badge > 0 ? `${label}, ${badge}` : label;
  return (
    <Link
      to={to}
      data-testid={testId}
      aria-label={ariaLabel}
      aria-current={active ? "page" : undefined}
      className={`relative flex min-h-12 min-w-[48px] flex-col items-center justify-center gap-0.5 px-2 py-1.5 text-[11px] font-semibold ${
        active ? "text-matpro-green-dark" : "text-matpro-anthracite-muted"
      }`}
    >
      <div className="relative" aria-hidden>
        <Icon className="h-5 w-5" strokeWidth={active ? 2.5 : 2} />
        {badge > 0 && (
          <span className="absolute -right-2 -top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-matpro-green px-1 text-[9px] font-bold text-white">
            {badge}
          </span>
        )}
      </div>
      <span aria-hidden>{label}</span>
    </Link>
  );
}

function BottomTabAction({ to, icon: Icon, label, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="-mt-4 flex flex-col items-center justify-center gap-0.5 rounded-md"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-md bg-matpro-green text-white shadow-md">
        <Icon className="h-6 w-6" strokeWidth={2.5} />
      </div>
      <span className="text-[11px] font-bold text-matpro-anthracite">{label}</span>
    </Link>
  );
}
